import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const DeskProModal = ({
  open,
  onClose,
  onCredentialsSaved,
  initialCredentials,
  tenantId,
  productName = 'Chattitude',
}) => {
  const [credentials, setCredentials] = useState({
    domain: '',
    key: ''
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCredentials = async () => {
      setLoading(true);
      setError(null);
      try {
        const jwtToken = Cookies.getJwtToken();
        const tenant = Cookies.getTenantId();
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

        const res = await axios.get(`${API_URL}/integrations/deskpro`, { headers });
        if (res.data) {
          setCredentials({
            domain: res.data.domain || '',
            key: res.data.key || ''
          });
        } else {
          setCredentials({ domain: '', key: '' });
        }
      } catch (err) {
        setCredentials({ domain: '', key: '' });
        setError('Failed to fetch credentials.');
      } finally {
        setLoading(false);
      }
    };
    if (open) {
      fetchCredentials();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!credentials.domain) return 'Domain is required.';
    if (!credentials.key) return 'Key is required.';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const jwtToken = Cookies.getJwtToken();
      const tenant = Cookies.getTenantId();
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

      await axios.post(`${API_URL}/integrations/deskpro`, credentials, { headers });
      setSnackbar({ open: true, message: 'Credentials saved successfully!', severity: 'success' });
      onCredentialsSaved && onCredentialsSaved(credentials);
      setTimeout(() => {
        onClose && onClose();
      }, 2000);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save credentials.', severity: 'error' });
      setError('Failed to save credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseIcon = () => {
    onClose && onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        sx={{ zIndex: 99999 }}
        PaperProps={{ style: { minWidth: 1200, minHeight: 800, zIndex: 99999 } }}
      >
        <DialogTitle sx={{ position: 'relative', pr: 6 }}>
          Desk Pro Credentials
          <IconButton
            onClick={handleCloseIcon}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Domain"
                name="domain"
                value={credentials.domain}
                onChange={handleChange}
                required
                fullWidth
              />
              <TextField
                label="Key"
                name="key"
                value={credentials.key}
                onChange={handleChange}
                required
                fullWidth
              />
              <Box sx={{ mt: 3, mb: 2, p: 2, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>How to Connect Deskpro</Typography>
                For the Deskpro integration, you will need two items: your Deskpro custom domain (for example, <b>yourcompany.deskpro.com</b>) and your API key.
                 
                <ol style={{ paddingLeft: 18, marginBottom: 8 }}>
                  <li>Login to your Deskpro Dashboard and look at your browser bar to find your Deskpro URL. Copy the domain into the <b>Domain</b> form field above.</li>
                  <li>Once logged in, click <b>Admin</b> (gear icon) in the left navigation, choose <b>APPS &amp; INTEGRATIONS</b>, and then choose <b>API Keys</b>.</li>
                  <li>Click the <b>+ NEW</b> button at the top far right of the screen. This will open a side panel on the right side with the API key options.</li>
                  <li>
                    Next we will provide the configuration for the API key:
                    <ul style={{ marginTop: 4 }}>
                      <li>In the <b>Name</b> field enter <b>{productName}</b>.</li>
                      <li>You can keep the Hourly and Daily Limits default switched off.</li>
                      <li>Under the <b>API Versions</b> section, check the box for <b>API v2</b>.</li>
                      <li>For the <b>API Key Context</b>, choose your agent (which should be your account name). Please note: don’t choose the <b>Machine Key</b> option.</li>
                      <li>You can leave the <b>Allow Tags</b> and <b>Disallow Tags</b> settings as default.</li>
                      <li>Click the <b>Save</b> button at the bottom to save your changes.</li>
                    </ul>
                  </li>
                  <li>{`Once the key has been generated, it will redirect you back to the API Keys page where you should now see your newly created ${productName} API key.`}</li>
                  <li>Under the <b>Code</b> column, click the <b>Show</b> button to display your API key. Copy the key and paste it in the above <b>API Key</b> form field.</li>
                  <li>Once you have both your domain and API key, click the <b>Save</b> button below to activate your integration.</li>

                </ol>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {`After connecting, ${productName} will securely store your Deskpro credentials and automatically create contacts and tickets from your forms.`}
                </Typography>
              </Box>
              {error && (
                <Typography color="error" variant="body2">{error}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 2, px: 3 }}>
          <Button onClick={handleSave} variant="outlined" color="primary" disabled={loading} sx={{ minWidth: 180, py: 1.2 }}>
            SAVE &amp; Close
          </Button>
        </DialogActions>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Dialog>
    </>
  );
};

export default DeskProModal;
